// Utility functions for consistent resource ID mapping between UUID and integer identifiers

export interface ResourceIdentity {
  uuid: string;          // resources.id (UUID - primary key)
  resource_id: number;   // resources.resource_id (integer - legacy identifier)
  name: string;
  role: string;
  role_type: string;
  capacity?: string;     // Add capacity field to store resource capacity hours
}

/**
 * Safely converts a resource ID to string format for display purposes
 * Handles both UUID strings and integer values consistently
 */
export const safeResourceIdToString = (resourceId: any): string => {
  if (resourceId === null || resourceId === undefined) {
    return 'N/A';
  }
  
  // If it's already a string, return as-is
  if (typeof resourceId === 'string') {
    return resourceId;
  }
  
  // If it's a number, convert to string
  if (typeof resourceId === 'number') {
    return resourceId.toString();
  }
  
  // For any other type, convert to string safely
  return String(resourceId);
};

/**
 * Safely extracts UUID substring for display (first 8 characters)
 * Only applies substring if the value is a valid UUID string
 */
export const safeUuidSubstring = (resourceId: any, length: number = 8): string => {
  const stringId = safeResourceIdToString(resourceId);
  
  // Check if it looks like a UUID (contains hyphens and is long enough)
  if (stringId.includes('-') && stringId.length >= length + 4) {
    return stringId.substring(0, length);
  }
  
  // For non-UUID values, return the full string or truncate safely
  return stringId.length > length ? stringId.substring(0, length) : stringId;
};

/**
 * Ensures consistent UUID-based resource identification throughout the system
 */
export const normalizeResourceForAssignment = (resource: any): ResourceIdentity => {
  return {
    uuid: resource.id || resource.uuid,                    // Use resources.id (UUID) as primary
    resource_id: resource.resource_id || resource.id,      // Keep integer resource_id for compatibility
    name: resource.name,
    role: resource.role,
    role_type: resource.role_type || 'team-member',
    capacity: resource.capacity || '8'                     // Include capacity for allocation_hrs
  };
};

/**
 * Creates a resource assignment record with UUID consistency
 */
export const createResourceAssignmentRecord = (
  resource: ResourceIdentity, 
  projectId: string, 
  userId: string, 
  taskData: any
) => {
  return {
    user_id: userId,
    project_id: projectId,
    task_id: taskData.taskName || taskData.task_id || 'General Assignment',
    resource_id: resource.uuid,  // CRITICAL: Use UUID for resource_assignment.resource_id
    name: resource.name,
    role: resource.role,
    role_type: resource.role_type,
    rate: taskData.rate ?? null,
    start_date: taskData.startDate || new Date().toISOString().split('T')[0],
    end_date: taskData.endDate || new Date().toISOString().split('T')[0],
    duration: taskData.duration || 30,
    allocated_percentage: taskData.allocation || 100,
    utilization_percentage: taskData.allocation || 100,
    allocation_hrs: resource.capacity || '8' // Use resource capacity instead of percentage
  };
};

/**
 * Validates that a resource has consistent UUID identification
 */
export const validateResourceIdentity = (resource: any): boolean => {
  return !!(resource.uuid || resource.id) && !!resource.name && !!resource.role_type;
};

/**
 * Logs resource matching information for debugging
 */
export const logResourceMatching = (
  context: string,
  resources: ResourceIdentity[],
  additionalInfo?: any
) => {
  console.log(`🔍 RESOURCE MATCHING - ${context}:`, {
    total: resources.length,
    byRoleType: {
      'project-director': resources.filter(r => r.role_type === 'project-director').length,
      'project-manager': resources.filter(r => r.role_type === 'project-manager').length,
      'team-lead': resources.filter(r => r.role_type === 'team-lead').length,
      'team-member': resources.filter(r => r.role_type === 'team-member').length
    },
    sampleUuids: resources.slice(0, 3).map(r => ({
      name: r.name,
      uuid: r.uuid,
      resource_id: r.resource_id,
      role_type: r.role_type
    })),
    ...additionalInfo
  });
};
